import { defineStore } from 'pinia';
import type { PlayerVipLevel } from '~/types/player-profile';
import {
  clearPlayerProfileCache,
  fetchPlayerProfileHandler,
  readPlayerProfileCache,
} from '~/handlers/fetch-player-profile.handler';

// O que se sabe do jogador logado (GET /players/me): elegibilidade de aposta e
// nível VIP. Existe separado da store do check-in porque a resposta é sobre a
// CONTA, não sobre o modal — e porque quem depende dela (item do menu horizontal,
// gate da roletinha, card de VIP do Clube Fidelidade) precisa dela sem abrir nada.
//
// Antes a pergunta pegava carona em dois lugares: o board do check-in (o menu
// pedia período, trilha e claims inteiros só pra ler um booleano) e o perfil da
// Smartico (o tier saía das `core_public_tags`, o que exigia o SDK carregado e o
// `identify` concluído). Agora as duas respostas vêm da mesma rota, do tamanho da
// pergunta.
//
// Quem dispara e quem invalida é o plugin de sessão
// (plugins/player-profile.client.ts), não um componente: o dado é da sessão e não
// pode existir só enquanto uma tela específica estiver montada.

export const usePlayerProfileStore = defineStore('player-profile', () => {
  // Última conta que o core CONFIRMOU como elegível. É o ID, e não um boolean,
  // pelo mesmo motivo da store do check-in: no SPA dá pra trocar de login sem
  // recarregar, e um `true` solto viajaria pro próximo jogador.
  const eligibleUserId = ref<number | null>(null);

  const vip = ref<PlayerVipLevel | null>(null);

  // Última conta cujo perfil já foi RESPONDIDO (pelo core, ou pelo cache desta
  // aba enquanto a resposta não chega). Serve pra distinguir "não" de "ainda não sei", que são coisas
  // diferentes pra quem lê: enquanto a resposta não chega, todo mundo parece
  // jogador comum e inelegível.
  //
  // Só /players/me marca isto. O veredito que o board do check-in repassa
  // (acceptVerdict) responde elegibilidade, não nível — marcar ali seria afirmar
  // que se sabe o VIP quando ninguém perguntou por ele.
  const knownUserId = ref<number | null>(null);

  const { userId } = useUserData();

  // Sinal POSITIVO: só é elegível quem é um "sim" explícito pra ESTA conta.
  // Deslogado, resposta ausente, falha de rede — tudo isso é "não", porque um
  // "não sei" não pode virar convite.
  const isEligible = computed(
    () => !!userId.value && eligibleUserId.value === userId.value,
  );

  const isVip = computed(() => vip.value !== null);

  // "Já tenho resposta pra ESTA conta." Quem só enfeita (chip, selo) pode
  // ignorar; quem decide layout ou nega acesso precisa distinguir.
  const isProfileKnown = computed(
    () => !!userId.value && knownUserId.value === userId.value,
  );

  /**
   * Garante que se sabe o estado do jogador. A pergunta VAI ao core TODA VEZ —
   * não há freio de tempo nem memo que a engula; contra chamadores simultâneos
   * sobra só o compartilhamento da requisição em vôo, dentro do handler.
   *
   * Antes de esperar, hidrata do cache desta aba (última resposta conhecida) pra
   * a tela não piscar no F5 — quem lê `vip` monta o card certo de cara. O que o
   * core responder sobrescreve isso, inclusive pra baixo.
   */
  async function ensure() {
    const id = userId.value;

    if (!id) return;

    // Só hidrata quem ainda não tem resposta pra ESTA conta: numa chamada
    // seguinte (foco de aba) o que está na store já é mais fresco que o cache.
    if (knownUserId.value !== id) {
      const cached = readPlayerProfileCache(id);

      if (cached) {
        eligibleUserId.value = id;
        vip.value = cached.vip;
        knownUserId.value = id;
      }
    }

    const profile = await fetchPlayerProfileHandler({ userId: id });

    // null = a busca falhou. Preserva o que já se sabia: silêncio não rebaixa
    // jogador.
    if (!profile) return;

    // A conta pode ter trocado enquanto a resposta vinha; o veredito é de quem
    // foi perguntado.
    if (userId.value !== id) return;

    eligibleUserId.value = profile.isEligible ? id : null;
    vip.value = profile.vip;
    knownUserId.value = id;
  }

  /**
   * Aceita o "sim" que o board do check-in trouxe. Existe pra as duas rotas não
   * manterem contas paralelas do mesmo fato: desde que o core passou a memoizar
   * a elegibilidade num lugar só, board e players/me leem a MESMA flag — então o
   * que chegar primeiro serve.
   *
   * Só SOBE, nunca rebaixa, e isso não é economia de código: elegibilidade é
   * monotônica (aposta não se desfaz), então um `false` vindo daqui não é notícia
   * nova sobre o jogador — é ausência de notícia. E o board tem um caminho que
   * produz `false` sem ninguém ter dito nada: quando a busca falha sem cache, o
   * handler devolve o board sintético vazio (`EMPTY`, `isEligible: false`) e o
   * `load()` não consegue distinguir isso de uma resposta real.
   *
   * Aceitar esse `false` custava caro porque `isProfileKnown` continua ligado (o
   * /players/me já respondeu): o jogador virava "sabidamente inelegível" e a
   * roletinha passava a abrir direto no aviso de indisponível, sem nem consultar
   * o core, até o próximo refresh por foco restaurar o "sim" do memo. Uma queda
   * de rede no check-in desligava a roleta de quem tem direito a ela.
   *
   * Quem responde "este jogador não participa" é o /players/me, no `ensure()` —
   * a rota feita pra essa pergunta.
   */
  function acceptVerdict(id: number, isPlayerEligible: boolean) {
    if (!id || userId.value !== id) return;
    if (!isPlayerEligible) return;

    eligibleUserId.value = id;
  }

  /** Troca de conta / logout: o que se sabia era daquele jogador. */
  function reset(previousUserId?: number) {
    eligibleUserId.value = null;
    vip.value = null;
    knownUserId.value = null;
    clearPlayerProfileCache(previousUserId);
  }

  return {
    eligibleUserId,
    vip,
    isEligible,
    isVip,
    isProfileKnown,
    ensure,
    acceptVerdict,
    reset,
  };
});
